import React, { useState, useEffect } from 'react';
import { format, subDays } from 'date-fns';
import { User } from '@supabase/supabase-js';
import { Loader2, FileDown, Filter } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { InventoryItem } from '@/types/inventory';
import ReportSettings from './ReportSettings';
import ReportPreview from './ReportPreview';
import { 
  calculateStats, 
  generateAiSummary, 
  generatePdfReport 
} from './exportUtils';

interface ExportReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inventoryItems: InventoryItem[];
  user: User | null;
}

export default function ExportReportModal({ 
  open, 
  onOpenChange, 
  inventoryItems, 
  user 
}: ExportReportModalProps) {
  const [dateRange, setDateRange] = useState<'7' | '30' | 'custom'>('7');
  const [fromDate, setFromDate] = useState<Date | undefined>(subDays(new Date(), 7));
  const [toDate, setToDate] = useState<Date | undefined>(new Date());
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
  const [filterMode, setFilterMode] = useState<'all' | 'selected'>('all');
  const [showPreview, setShowPreview] = useState(false);
  const [generatingAiSummary, setGeneratingAiSummary] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDateRange('7');
      setFromDate(subDays(new Date(), 7));
      setToDate(new Date());
      setSelectedItems({});
      setFilterMode('all');
      setShowPreview(false);
      setAiSummary(null);
    }
  }, [open]);

  useEffect(() => {
    if (dateRange === '7') {
      setFromDate(subDays(new Date(), 7));
      setToDate(new Date());
    } else if (dateRange === '30') {
      setFromDate(subDays(new Date(), 30));
      setToDate(new Date());
    }
  }, [dateRange]);

  useEffect(() => {
    const initialSelectedItems: Record<string, boolean> = {};
    inventoryItems.forEach(item => {
      initialSelectedItems[item.id] = false;
    });
    setSelectedItems(initialSelectedItems);
  }, [inventoryItems]);

  const getFilteredItems = () => {
    let filteredByDate = inventoryItems;
    
    if (fromDate) {
      filteredByDate = inventoryItems.filter(item => {
        const itemDate = new Date(item.created_at);
        return itemDate >= fromDate && itemDate <= (toDate || new Date());
      });
    }
    
    if (filterMode === 'selected') {
      return filteredByDate.filter(item => selectedItems[item.id]);
    }
    
    return filteredByDate;
  };

  const toggleAllItems = (checked: boolean) => {
    const newSelectedItems = { ...selectedItems };
    Object.keys(newSelectedItems).forEach(id => {
      newSelectedItems[id] = checked;
    });
    setSelectedItems(newSelectedItems);
  };

  const toggleItem = (id: string, checked: boolean) => {
    setSelectedItems({
      ...selectedItems,
      [id]: checked
    });
  };

  const handlePreviewReport = async () => {
    const filteredItems = getFilteredItems();
    const stats = calculateStats(filteredItems);
    setGeneratingAiSummary(true);
    
    try {
      const summary = await generateAiSummary(stats, filteredItems);
      setAiSummary(summary);
      toast.success("AI summary generated successfully", {
        position: "bottom-right",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error generating AI summary:", error);
      toast.error("Failed to generate AI summary, using fallback", {
        position: "bottom-right",
        duration: 3000,
      });
    } finally {
      setGeneratingAiSummary(false);
    }
    
    setShowPreview(true);
  };

  const generateReport = async () => {
    setIsGenerating(true);
    
    try {
      const filteredItems = getFilteredItems();
      const stats = calculateStats(filteredItems);
      
      let summaryForReport = aiSummary;
      
      if (!summaryForReport) {
        console.log("No existing AI summary, generating now...");
        try {
          summaryForReport = await generateAiSummary(stats, filteredItems);
          setAiSummary(summaryForReport);
        } catch (error) {
          console.error("Error generating AI summary during PDF export:", error);
          summaryForReport = null;
        }
      }
      
      console.log("Generating PDF with AI summary:", summaryForReport?.substring(0, 100) || "None");
      
      const fileName = await generatePdfReport(
        filteredItems, 
        stats, 
        summaryForReport, 
        user, 
        fromDate,
        toDate
      );
      
      toast.success(`Report "${fileName}" generated successfully`, {
        position: "bottom-right",
        duration: 3000,
      });
      
      onOpenChange(false);
      
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report", {
        position: "bottom-right",
        duration: 3000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "sm:max-w-[650px]", 
        showPreview ? "sm:max-h-[90vh] overflow-y-auto" : ""
      )}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="h-5 w-5 text-emerald-600" />
            Export Inventory Report
          </DialogTitle>
          <DialogDescription>
            Generate a detailed PDF report of your inventory data with charts and insights.
          </DialogDescription>
        </DialogHeader>
        
        {!showPreview ? (
          <ReportSettings 
            dateRange={dateRange}
            setDateRange={setDateRange}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            filterMode={filterMode}
            setFilterMode={setFilterMode}
            inventoryItems={inventoryItems}
            selectedItems={selectedItems}
            toggleAllItems={toggleAllItems}
            toggleItem={toggleItem}
            onPreview={handlePreviewReport}
          />
        ) : (
          <ReportPreview 
            filteredItems={getFilteredItems()}
            aiSummary={aiSummary}
            generatingAiSummary={generatingAiSummary}
            onBack={() => setShowPreview(false)}
          />
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          
          <Button 
            onClick={generateReport} 
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                Generating...
              </>
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" /> 
                Generate PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
